import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DownloadIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';

interface DownloadModalProps {
  open: boolean;
  onClose: () => void;
  onDownload: (filename: string) => void;
  defaultFilename: string;
  title?: string;
  description?: string;
  confirmLabel?: React.ReactNode;
}

export const DownloadModal: React.FC<DownloadModalProps> = ({
  open,
  onClose,
  onDownload,
  defaultFilename,
  title,
  description,
  confirmLabel,
}) => {
  const gt = useGT();
  const resolvedTitle =
    title ?? gt('Download Report', { context: 'download dialog title' });
  const resolvedDescription =
    description ??
    gt('Choose a filename for your download', {
      context: 'download dialog description',
    });
  const [filename, setFilename] = useState(defaultFilename);

  // Update filename when defaultFilename changes
  useEffect(() => {
    setFilename(defaultFilename);
  }, [defaultFilename]);

  const handleDownload = () => {
    onDownload(filename);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleDownload();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{resolvedTitle}</DialogTitle>
          <DialogDescription>{resolvedDescription}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="filename">
              <T context="label for download filename field">Filename</T>
            </Label>
            <Input
              id="filename"
              value={filename}
              onChange={(e) => setFilename(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={gt('Enter filename...', {
                context: 'placeholder for download filename input',
              })}
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              <T context="help text for download filename">
                A timestamp will be automatically added to the filename
              </T>
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            <T context="button to cancel download dialog">Cancel</T>
          </Button>
          <Button onClick={handleDownload}>
            <DownloadIcon className="h-4 w-4 mr-2" />
            {confirmLabel ?? (
              <T context="button to confirm file download">Download</T>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
